import React, { useEffect, useRef, useCallback, useLayoutEffect } from "react";
import { useAuth } from "../../../context/AuthContext";
import { useThread } from "../../../context/ThreadContext";
import { useSocket } from "../../../context/SocketContext";
import { useChat } from "../../../context/ChatContext";
import ThreadMessage from "./ThreadMessage";
import ThreadComposer from "./ThreadComposer";
import ThreadTypingIndicator from "./ThreadTypingIndicator";
import MessageBubble from "../../chat/components/MessageBubble";
import AppIcon from "../../../components/ui/AppIcon";
import "./ThreadModal.css";

export default function ThreadModal({ onClose }) {
  const { user } = useAuth();
  const { socket } = useSocket();
  const { activeChat } = useChat();
  const {
    openThread,
    threadReplies,
    threadMetadata,
    threadLoading,
    threadHasMore,
    threadTypingUsers,
    loadThreadReplies,
    sendThreadReply,
    markAsRead,
    loadMoreReplies,
    startThreadTyping,
    stopThreadTyping,
  } = useThread();

  const listRef = useRef(null);
  const bottomRef = useRef(null);
  const parentMessage = openThread?.parentMessage;
  const parentMessageId = parentMessage?._id || parentMessage?.id;

  // Normalize parentMessage for MessageBubble
  const normalizedParentMessage = parentMessage
    ? {
        ...parentMessage,
        text: parentMessage.text || parentMessage.content || "",
        content: undefined,
        senderInfo:
          parentMessage.senderInfo || parentMessage.sender
            ? {
                _id: parentMessage.sender?._id || parentMessage.senderId,
                name:
                  parentMessage.sender?.name ||
                  parentMessage.senderName ||
                  "Unknown",
                profilePhoto:
                  parentMessage.sender?.profilePhoto ||
                  parentMessage.senderAvatar,
              }
            : {
                _id: parentMessage.senderId,
                name: parentMessage.senderName || "Unknown",
                profilePhoto: parentMessage.senderAvatar,
              },
      }
    : null;

  // Load replies when thread opens — markAsRead is fire-and-forget
  useEffect(() => {
    if (parentMessageId) {
      loadThreadReplies(parentMessageId, 1);
      markAsRead(parentMessageId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentMessageId]);

  // Scroll to bottom after replies load or a new reply is added.
  // useLayoutEffect fires synchronously after DOM mutations — no visible flash.
  useLayoutEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: "auto" });
    }
  }, [threadReplies.length]);

  const handleScroll = useCallback(() => {
    if (
      listRef.current &&
      listRef.current.scrollTop === 0 &&
      !threadLoading &&
      threadHasMore
    ) {
      loadMoreReplies();
    }
  }, [threadLoading, threadHasMore, loadMoreReplies]);

  const handleReaction = useCallback(
    (messageId, emoji) => {
      if (!socket || !user) return;
      const chatId =
        openThread?.chatId ||
        (activeChat ? activeChat.data._id || activeChat.data.id : null);
      const chatType = openThread?.chatType || activeChat?.type;
      if (!chatId || !chatType) return;
      console.log("[ThreadModal handleReaction] emitting with:", {
        messageId,
        emoji,
        chatId,
        chatType,
        userId: user._id,
      });
      socket.emit("reaction-added", {
        messageId,
        emoji,
        userId: user._id,
        userName: user.name,
        targetId: chatId,
        type: chatType,
      });
    },
    [socket, user, openThread, activeChat],
  );

  if (!openThread) return null;

  return (
    <div className="thread-modal-overlay" onClick={onClose}>
      <div
        className="thread-modal-container"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="thread-modal-header">
          <div className="thread-modal-header-content">
            <h2 className="thread-modal-title">
              <span className="thread-modal-title-icon">
                <AppIcon name="ChatBubbleBottomCenterIcon" size={16} />
              </span>
              Thread
            </h2>
            <button className="thread-modal-close-btn" onClick={onClose}>
              <AppIcon name="XMarkIcon" size={20} />
            </button>
          </div>
          {threadMetadata.replyCount > 0 && (
            <div className="thread-modal-subtitle">
              {threadMetadata.replyCount}{" "}
              {threadMetadata.replyCount === 1 ? "reply" : "replies"}
              <span className="thread-modal-subtitle-dot" />
              {threadMetadata.lastReplyAt && (
                <span>
                  Last activity {formatTimeAgo(threadMetadata.lastReplyAt)}
                </span>
              )}
            </div>
          )}
        </div>

        <div
          className="thread-modal-messages"
          ref={listRef}
          onScroll={handleScroll}
        >
          <div className="thread-modal-parent">
            <div className="thread-modal-parent-label">
              <AppIcon
                name="ChatBubbleBottomCenterIcon"
                size={14}
                className="thread-modal-parent-label-icon"
              />
              Parent Message
            </div>
            <MessageBubble
              message={normalizedParentMessage}
              isOwn={isOwnMessage(parentMessage, user)}
              hideAvatar={false}
              hideSenderName={false}
              onReaction={handleReaction}
            />
          </div>

          <div className="thread-modal-replies">
            {(threadReplies || []).map((reply, index) => {
              const senderId = reply.senderId?._id || reply.senderId;
              const isOwn =
                senderId === user._id ||
                senderId?.toString() === user._id?.toString();
              return (
                <ThreadMessage
                  key={reply._id || index}
                  reply={reply}
                  isOwn={isOwn}
                />
              );
            })}
          </div>

          {/* Loading spinner — only shown on initial load, not on reply send */}
          {threadLoading && threadReplies.length === 0 && (
            <div className="thread-modal-loading">
              <AppIcon name="Spinner" className="animate-spin" />
            </div>
          )}

          {/* Scroll anchor — always at the bottom of the list */}
          <div ref={bottomRef} />
        </div>

        <ThreadComposer
          onSend={sendThreadReply}
          onTypingStart={startThreadTyping}
          onTypingStop={stopThreadTyping}
        />

        <ThreadTypingIndicator users={threadTypingUsers} />
      </div>
    </div>
  );
}

function isOwnMessage(message, user) {
  const senderId = message.senderId?._id || message.senderId;
  return senderId === user._id || senderId?.toString() === user._id?.toString();
}

function formatTimeAgo(date) {
  if (!date) return "";
  const now = new Date();
  const past = new Date(date);
  const diffMs = now - past;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return past.toLocaleDateString();
}
